import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { toPageError } from '../partners/PartnerListPage';
import {
  apiOperationsRepositoryToken,
  type ApiOperationsFilters,
  type FailedApiSubmission,
} from './mockRepository';

const initialFilters: ApiOperationsFilters = { endpoint: '', statusClass: '' };

export function ApiOperationsPage() {
  const { t } = useTranslation();
  const repository = useRepository(apiOperationsRepositoryToken);
  const [filters, setFilters] = useState(initialFilters);
  const resource = useResource(
    (signal) => repository.getSnapshot(filters, signal),
    [repository, filters],
    () => false,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source="mock" />}
        subtitle={t('apiOperations.subtitle')}
        title={t('apiOperations.title')}
      />
      <ListFilterPanel
        ariaLabel={t('filterPanel.label', { page: t('apiOperations.title') })}
        primaryFields={
          <>
            <FormControl size="small">
              <InputLabel id="api-operations-endpoint-label">
                {t('apiOperations.filters.endpoint')}
              </InputLabel>
              <Select
                label={t('apiOperations.filters.endpoint')}
                labelId="api-operations-endpoint-label"
                onChange={(event) =>
                  setFilters((current) => ({ ...current, endpoint: event.target.value }))
                }
                value={filters.endpoint}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="POST /api/v1/transactions">POST /api/v1/transactions</MenuItem>
                <MenuItem value="GET /api/v1/transactions">GET /api/v1/transactions</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel id="api-operations-status-label">
                {t('apiOperations.filters.statusClass')}
              </InputLabel>
              <Select
                label={t('apiOperations.filters.statusClass')}
                labelId="api-operations-status-label"
                onChange={(event) =>
                  setFilters((current) => ({ ...current, statusClass: event.target.value }))
                }
                value={filters.statusClass}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="4xx">4xx</MenuItem>
                <MenuItem value="5xx">5xx</MenuItem>
              </Select>
            </FormControl>
          </>
        }
      />
      <PageState
        error={toPageError(resource.error, t('apiOperations.errors.load'))}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <Box
              aria-label={t('apiOperations.metrics.label')}
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              }}
            >
              <MetricCard
                label={t('apiOperations.metrics.requestVolume')}
                value={String(resource.data.metric.requestVolume)}
              />
              <MetricCard
                label={t('apiOperations.metrics.errorRate')}
                value={resource.data.metric.errorRate}
              />
              <MetricCard
                label={t('apiOperations.metrics.p50Latency')}
                value={`${resource.data.metric.p50LatencyMs} ms`}
              />
              <MetricCard
                label={t('apiOperations.metrics.p95Latency')}
                value={`${resource.data.metric.p95LatencyMs} ms`}
              />
              <MetricCard
                label={t('apiOperations.metrics.p99Latency')}
                value={`${resource.data.metric.p99LatencyMs} ms`}
              />
            </Box>
            <FailedSubmissions items={resource.data.failedSubmissions} />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function MetricCard({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography color="text.secondary" variant="body2">
          {label}
        </Typography>
        <Typography component="p" variant="h5">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function FailedSubmissions({ items }: { readonly items: readonly FailedApiSubmission[] }) {
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (mobile)
    return (
      <Stack aria-label={t('apiOperations.failures.label')} component="ul" spacing={1}>
        {items.map((item) => (
          <Card component="li" key={item.id} variant="outlined">
            <CardContent>
              <Typography component="h2" variant="subtitle1">
                {item.endpoint}
              </Typography>
              <Typography>
                {item.status} · {item.errorCode}
              </Typography>
              <Typography variant="body2">{item.partner}</Typography>
              <Typography variant="body2">
                {t('apiOperations.failures.occurredAt')}: {displayTime(item.occurredAt, language)}
              </Typography>
              <Typography variant="body2">{item.requestId}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table aria-label={t('apiOperations.failures.label')} size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('apiOperations.filters.endpoint')}</TableCell>
            <TableCell>{t('apiOperations.failures.status')}</TableCell>
            <TableCell>{t('apiOperations.failures.partner')}</TableCell>
            <TableCell>{t('apiOperations.failures.errorCode')}</TableCell>
            <TableCell>{t('apiOperations.failures.occurredAt')}</TableCell>
            <TableCell>{t('apiOperations.failures.requestId')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.endpoint}</TableCell>
              <TableCell>{item.status}</TableCell>
              <TableCell>{item.partner}</TableCell>
              <TableCell>{item.errorCode}</TableCell>
              <TableCell>{displayTime(item.occurredAt, language)}</TableCell>
              <TableCell>{item.requestId}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function displayTime(timestamp: string, language: TimeLanguage): string {
  return formatTimestamp(timestamp, 'Asia/Bangkok', language).display;
}
